import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { NarrativeCard, Pill, ToolbarSelect, ProgressBar, Stat, Sparkline, EmptyState } from "@/design/components";
import { formatPct, formatHumanCurrency } from "@/design/format";
import { useSeed, useProjects, useMoney, useActiveProfile, useTerminology } from "@/domain/hooks";
import { computeHealth } from "@/screens/programme/ProjectHealthTable";
import { useOrgIndex } from "@/domain/hooks";
import { isOpenProjectStatus } from "@/domain/status";
import { CashExposureCard } from "./CashExposureCard";
import { HeimdallPanel } from "./HeimdallPanel";
import type { AnalyticsSeed } from "@/domain/types";
import { cn } from "@/design/cn";

export function AnalyticsSurface({
  scopeLabel = "This programme",
  lens,
}: {
  scopeLabel?: string;
  lens?: "funder" | "stratus" | "contractor";
}) {
  const seed = useSeed();
  const a = seed.analytics;
  const money = useMoney();
  const profile = useActiveProfile();
  const projects = useProjects();
  const orgs = useOrgIndex();
  // Lens drives the attention content; default it from the active profile so the demo
  // switcher controls it and no role sees another's operational items (P0.4).
  const effLens = lens ?? (profile.group === "funder" ? "funder" : profile.group === "contractor" ? "contractor" : "stratus");
  // The funder lens is a trimmed trustee view: keep the on-track / on-budget / projection
  // judgment, hide the operator-only forensics (cost-driver attribution, the in-jeopardy
  // list, Heimdall review volumes and cash-rails exposure) that a trustee never actions.
  const showForensics = effLens !== "funder";
  const readyDisb = seed.disbursements.filter((d) => d.state === "ready_for_funder");
  const ownPending = seed.spendRequests.filter(
    (r) => r.organisationId === profile.organisationId && (r.state === "proposed" || r.state === "flagged"),
  );
  const [compare, setCompare] = useState(false);
  const [scope, setScope] = useState("programme");
  const [period, setPeriod] = useState("quarter");

  // ----------------------------------------------------------------------------
  // Narrative derivation (honest-figures): every number and named entity in the
  // four breakdown bodies is computed from seed.analytics + the active projects/orgs,
  // so the prose can never contradict the figure chips beside it or carry another
  // seed's entities. No literal figures or names live in the strings.
  // ----------------------------------------------------------------------------
  const msDriftDays = (m: AnalyticsSeed["milestones"][number]) =>
    Math.round((new Date(m.projectedDate).getTime() - new Date(m.plannedDate).getTime()) / 86_400_000);
  const onTrackCount = a.milestones.filter((m) => m.onTrack).length;
  const totalMs = a.milestones.length;
  // Worst off-track milestone drives the "at risk" sentence; null if all on track.
  const worstMs = a.milestones
    .filter((m) => !m.onTrack)
    .sort((x, y) => msDriftDays(y) - msDriftDays(x))[0];
  const worstMsDrift = worstMs ? msDriftDays(worstMs) : 0;

  // Contractors running ahead of plan among active projects (negative schedule drift).
  const aheadProjects = projects
    .filter((p) => isOpenProjectStatus(p.status) && (p.scheduleDriftDays ?? 0) < 0)
    .sort((x, y) => (x.scheduleDriftDays ?? 0) - (y.scheduleDriftDays ?? 0));
  const aheadNamesList = aheadProjects
    .map((p) => orgs[p.leadContractorId]?.name)
    .filter((n): n is string => !!n);
  // De-duplicate (one contractor may lead several sites) and join with an Oxford-free list.
  const aheadNames = [...new Set(aheadNamesList)];
  const aheadNamesText =
    aheadNames.length <= 1
      ? aheadNames.join("")
      : `${aheadNames.slice(0, -1).join(", ")} and ${aheadNames[aheadNames.length - 1]}`;

  // Top cost-driver attributions, excluding the residual "Unattributed" bucket.
  const drivers = a.costDrivers.attributions.filter((x) => x.factor.toLowerCase() !== "unattributed");
  const primaryDriver = drivers[0];
  const secondaryDriver = drivers[1];

  // Projected over-budget figure in money, derived from the projected ratio × programme budget.
  const projectedOverBudgetMoney = formatHumanCurrency(
    Math.round(a.projection.projectedOverBudgetPct * seed.programme.totalBudget),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ToolbarSelect value={scope} onChange={setScope} options={[{ value: "all", label: "All programmes" }, { value: "programme", label: scopeLabel }]} />
          <ToolbarSelect value={period} onChange={setPeriod} options={[{ value: "month", label: "This month" }, { value: "quarter", label: "This quarter" }, { value: "ptd", label: "Programme to date" }]} />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-mute">
          <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} className="accent-[var(--accent-base)]" />
          Show comparison to similar programmes
        </label>
      </div>

      {/* Central overview */}
      <OverviewCards />

      {/* Operator forensics: AI-review volumes and cash-rails exposure (hidden for funders). */}
      {/* TODO(heimdall): AI analyst not built yet — re-enable the HeimdallPanel when Heimdall ships. */}
      {/* {showForensics && <HeimdallPanel />} */}

      {showForensics && <CashExposureCard />}

      <div className="flex items-center gap-3">
        <span className="text-meta text-faint">Detailed breakdown</span>
        <span className="h-px flex-1 bg-line" aria-hidden />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Q1 — on time */}
      <Question number={1} heading="On time">
        <NarrativeCard
          variant="interpretation"
          title="On time"
          body={
            worstMs
              ? `Across the portfolio, ${onTrackCount} of ${totalMs} milestones are on track. The ${worstMs.name} milestone is at risk; current pace projects a ${worstMsDrift}-day overrun. The remaining ${totalMs - 1} hold their plan.`
              : `Across the portfolio, all ${totalMs} milestones are on track and holding their plan.`
          }
          figures={[
            { label: "Worst-case drift", value: `+${worstMsDrift} days vs plan`, tone: worstMs ? "warn" : undefined },
            { label: "Milestones on time", value: `${onTrackCount} of ${totalMs}` },
          ]}
        />
        <MilestoneDriftChart milestones={a.milestones} />
        {compare && <Comparison text={`Your pace is ${formatPct(Math.abs(a.budget.burnRateVsPlanPct), 1)} ahead of plan; comparable programmes at this stage are ${formatPct(Math.abs(a.benchmark.medianPaceVsPlanPct), 1)} behind. Faster than typical.`} />}
      </Question>

      {/* Q2 — on budget */}
      <Question number={2} heading="On budget">
        <NarrativeCard
          variant="interpretation"
          title="On budget"
          body={`Spend is ${formatPct(a.budget.consumedPct, 0)} of budget against ${formatPct(a.budget.timeElapsedPct, 0)} of time elapsed, a ${formatPct(a.budget.burnRateVsPlanPct, 1)} pace ahead of plan.${
            aheadNames.length
              ? ` ${aheadNames.length === 1 ? "One contractor is" : `${aheadNames.length} contractors are`} running ahead of pace: ${aheadNamesText}.`
              : ""
          } The confidence range tracks the median over-budget read of ${formatPct(a.benchmark.medianBudgetOverrunPct, 1)} across comparable programmes.`}
          figures={[
            { label: "Budget consumed", value: formatPct(a.budget.consumedPct, 1) },
            { label: "Time elapsed", value: formatPct(a.budget.timeElapsedPct, 1) },
            { label: "Burn vs plan", value: `+${formatPct(a.budget.burnRateVsPlanPct, 1)}`, tone: "warn" },
          ]}
        />
        <BudgetVsTime budget={a.budget} />
      </Question>

      {/* Q3 — if ballooning, why (operator forensic: cost-driver attribution; hidden for funders) */}
      {showForensics && (
      <Question number={3} heading="If costs are climbing, where">
        <NarrativeCard
          variant="interpretation"
          title="If costs are climbing, where"
          body={`The ${formatPct(a.costDrivers.overrunPct, 0)} overrun is concentrated in ${primaryDriver ? primaryDriver.factor.toLowerCase() : "unattributed costs"}, accounting for an estimated ${primaryDriver ? formatPct(primaryDriver.contributionPct, 1) : formatPct(a.costDrivers.overrunPct, 1)} of the total${primaryDriver?.source ? ` (${primaryDriver.source})` : ""}.${
            secondaryDriver
              ? ` The remainder appears in ${secondaryDriver.factor.toLowerCase()}, a further ${formatPct(secondaryDriver.contributionPct, 1)}${secondaryDriver.source ? ` (${secondaryDriver.source})` : ""}.`
              : ""
          }`}
          figures={[
            { label: "Overrun", value: formatPct(a.costDrivers.overrunPct, 1), tone: "warn" },
            ...(primaryDriver ? [{ label: primaryDriver.factor, value: formatPct(primaryDriver.contributionPct, 1) }] : []),
            ...(secondaryDriver ? [{ label: secondaryDriver.factor, value: formatPct(secondaryDriver.contributionPct, 1) }] : []),
          ]}
        />
        <OverrunBreakdown attributions={a.costDrivers.attributions} />
        <div className="flex flex-wrap gap-2">
          {primaryDriver && <Pill variant="warn">{primaryDriver.factor} · {formatPct(primaryDriver.contributionPct, 1)}</Pill>}
          {secondaryDriver && <Pill variant="neutral">{secondaryDriver.factor} · {formatPct(secondaryDriver.contributionPct, 1)}</Pill>}
        </div>
      </Question>
      )}

      {/* Q4 — future milestones */}
      <Question number={4} heading="What this means going forward">
        <NarrativeCard
          variant="interpretation"
          title="What this means going forward"
          body={`If the current pace continues${
            worstMs ? `, ${worstMs.name} is projected to slip ${worstMsDrift} days` : ""
          }. Total programme drift on this trajectory: +${a.projection.projectedDateDriftDays} days, an estimated ${projectedOverBudgetMoney} over plan (${formatPct(a.projection.projectedOverBudgetPct, 1)} of programme budget). This remains within the programme contingency buffer.`}
          figures={[
            { label: "Projected drift", value: `+${a.projection.projectedDateDriftDays} days`, tone: "warn" },
            { label: "Projected over budget", value: formatPct(a.projection.projectedOverBudgetPct, 1) },
          ]}
        />
        <MilestoneDriftChart milestones={a.milestones} dual />
        {/* The named "in jeopardy" sites are an operator drill-down, not a trustee concern. */}
        {showForensics && <PredictiveOutlook />}
      </Question>

      </div>

      {/* Q5 — attention, lens-scoped so no role sees another's operational items (P0.4) */}
      {effLens === "stratus" ? (
        <Question heading="Today's attention list">
          <NarrativeCard variant="flag" title="Where Stratus should focus today" body="Items ranked by urgency across the portfolio." />
          <ol className="space-y-2">
            {a.attentionList.map((item, i) => (
              <li key={item.id} className="flex items-start gap-3 rounded-card border border-line bg-card p-3">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-subtle text-[11px] font-semibold tabular-nums text-mute">{i + 1}</span>
                <span className="flex-1 text-sm text-ink">{item.body}</span>
                <span className="text-sm font-medium tabular-nums text-mute">{item.figure}</span>
              </li>
            ))}
          </ol>
        </Question>
      ) : effLens === "funder" ? (
        <Question heading="Awaiting your action">
          <NarrativeCard variant="flag" title="Awaiting your action" body={`${readyDisb.length} milestone disbursement${readyDisb.length === 1 ? "" : "s"} routed for your decision.`} />
          {readyDisb.length === 0 ? (
            <EmptyState title="Nothing awaiting your action" body="Routed milestone disbursements appear here for your decision." />
          ) : (
            <ol className="space-y-2">
              {readyDisb.map((d, i) => (
                <li key={d.id}>
                  <Link to="/funder/approvals" className="focus-ring flex items-start gap-3 rounded-card border border-line bg-card p-3 hover:bg-subtle">
                    <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-subtle text-[11px] font-semibold tabular-nums text-mute">{i + 1}</span>
                    <span className="flex-1 text-sm text-ink">{seed.milestones.find((m) => m.id === d.milestoneId)?.name ?? "Milestone disbursement"}</span>
                    <span className="text-sm font-medium tabular-nums text-mute">{money(d.amount)}</span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </Question>
      ) : (
        <Question heading="Your outstanding items">
          <NarrativeCard variant="flag" title="Your outstanding items" body="Asks awaiting action across your own scope." />
          {ownPending.length === 0 ? (
            <EmptyState title="Nothing outstanding" body="Asks needing your attention appear here." />
          ) : (
            <ol className="space-y-2">
              {ownPending.map((r, i) => (
                <li key={r.id} className="flex items-start gap-3 rounded-card border border-line bg-card p-3">
                  <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-subtle text-[11px] font-semibold tabular-nums text-mute">{i + 1}</span>
                  <span className="flex-1 text-sm text-ink">{r.title}</span>
                  <span className="text-sm font-medium tabular-nums text-mute">{money(r.amount)}</span>
                </li>
              ))}
            </ol>
          )}
        </Question>
      )}
    </div>
  );
}

/**
 * Central overview: a single glance at everything relevant before the detailed
 * breakdown. Status, schedule, budget, evidence, at-risk count, projected drift.
 */
function OverviewCards() {
  const projects = useProjects();
  const orgs = useOrgIndex();
  const seed = useSeed();
  const a = seed.analytics;
  const active = projects.filter((p) => isOpenProjectStatus(p.status));
  const health = active.map((p) => computeHealth(p, orgs[p.leadContractorId]?.name ?? "—"));
  const atRisk = health.filter((h) => h.overall === "red").length;
  const watch = health.filter((h) => h.overall === "amber").length;
  const onTrack = health.filter((h) => h.overall === "green").length;
  const overall = atRisk > 0 ? { label: "At risk", tone: "warn" as const } : watch > 0 ? { label: "Watch", tone: "warn" as const } : { label: "On track", tone: "ok" as const };
  const evCoverage = active.length
    ? Math.round((active.reduce((s, p) => s + (p.spent ? p.evidenced / p.spent : 0), 0) / active.length) * 100)
    : 0;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <Stat label="Overall status" value={overall.label} sub={`${onTrack} on track · ${watch} watch · ${atRisk} at risk`} subTone={overall.tone} />
      <Stat label="Schedule" value={`+${a.projection.projectedDateDriftDays}`} unit="days" sub="projected drift" subTone="warn" trend={<Sparkline data={[0, 2, 5, 8, 11, a.projection.projectedDateDriftDays]} tone="warn" />} />
      <Stat label="Budget consumed" value={formatPct(a.budget.consumedPct, 0)} sub={`vs ${formatPct(a.budget.timeElapsedPct, 0)} time elapsed`} subTone="warn" trend={<Sparkline data={[20, 27, 33, 39, 44, Math.round(a.budget.consumedPct * 100)]} />} />
      <Stat label="Evidence coverage" value={`${evCoverage}%`} sub="of spend evidenced" subTone={evCoverage >= 80 ? "ok" : "warn"} trend={<Sparkline data={[60, 66, 72, 76, 80, evCoverage]} tone="ok" />} />
      <Stat label="Active projects" value={`${active.length}`} sub={`${atRisk + watch} need attention`} subTone={atRisk + watch ? "warn" : "ok"} />
      <Stat label="Pace vs plan" value={`+${formatPct(a.budget.burnRateVsPlanPct, 1)}`} sub="faster than plan" subTone="warn" />
    </div>
  );
}

/**
 * Predictive panel: projects current pace forward a quarter and names the sites
 * (terminology-driven) most in jeopardy of falling behind. Wording reads for both
 * seeds — "fire stations" and "maternity wings" alike — via useTerminology.
 */
function PredictiveOutlook() {
  const projects = useProjects();
  const orgs = useOrgIndex();
  const seed = useSeed();
  const term = useTerminology();
  const ragColor = { red: "bg-bad", amber: "bg-warn", green: "bg-ok", neutral: "bg-faint" };
  const driftDays = seed.analytics.projection.projectedDateDriftDays;
  const projectWord = term.project.toLowerCase();

  const atRisk = projects
    .filter((p) => isOpenProjectStatus(p.status))
    .map((p) => ({ p, health: computeHealth(p, orgs[p.leadContractorId]?.name ?? "—"), projected: p.projectedDriftDays ?? 0 }))
    .filter((x) => x.projected > 5 || x.health.overall === "red")
    .sort((a, b) => b.projected - a.projected);

  return (
    <div className="rounded-card border border-line bg-card p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-5 items-center rounded-full bg-brandSoft px-2 text-[11px] font-semibold uppercase tracking-wide text-brand">Predictive</span>
        <p className="text-sm font-medium text-ink">Next-quarter outlook</p>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-mute">
        Based on the first three months of delivery, the model projects the programme to run about {driftDays} days behind by the end of next quarter. The slip is concentrated in {atRisk.length} {projectWord}{atRisk.length === 1 ? "" : "s"}; the remainder hold their plan. Acting on the items below in the next two weeks recovers most of the projected drift.
      </p>
      {atRisk.length > 0 && (
        <div className="mt-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">{term.project}s in jeopardy</p>
          <ul className="space-y-1.5">
            {atRisk.map(({ p, health, projected }) => (
              <li key={p.id} className="flex items-center gap-3 rounded-md border border-line px-3 py-2">
                <span className={cn("h-2 w-2 shrink-0 rounded-full", ragColor[health.overall])} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                  <p className="truncate text-xs text-faint">{orgs[p.leadContractorId]?.name} · {p.currentPhase}</p>
                </div>
                <Pill variant={projected > 12 ? "danger" : "warn"}>Projected +{projected} days</Pill>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// One analytics module: a heading over its narrative + chart. Laid out in a grid so the
// four breakdowns read two-up instead of a long scroll (design brief §6.8). Numbered
// circles are gone; size and weight order the page.
function Question({ heading, children }: { number?: number; heading: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold text-ink">{heading}</h3>
      {children}
    </section>
  );
}

function Comparison({ text }: { text: string }) {
  return <p className="rounded-md bg-subtle px-3 py-2 text-xs text-mute">{text}</p>;
}

function MilestoneDriftChart({ milestones, dual }: { milestones: AnalyticsSeed["milestones"]; dual?: boolean }) {
  const data = milestones.map((m) => {
    const planned = new Date(m.plannedDate).getTime();
    const projected = new Date(m.projectedDate).getTime();
    const driftDays = Math.round((projected - planned) / 86_400_000);
    return { name: m.name, drift: driftDays, onTrack: m.onTrack };
  });
  return (
    <div className="rounded-card border border-line bg-card p-4">
      <p className="mb-2 text-xs font-medium text-faint">{dual ? "Projected slip per milestone (days vs plan)" : "Drift vs plan (days)"}</p>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 20 }}>
          <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-tertiary)", fontVariant: "tabular-nums" }} />
          <YAxis type="category" dataKey="name" width={110} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--text-secondary)" }} />
          <RTooltip cursor={{ fill: "var(--bg-subtle)" }} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border-default)", boxShadow: "var(--tw-shadow, 0 8px 24px -8px rgba(16,24,40,0.16))" }} formatter={(v) => [`${v} days`, "drift"]} />
          <ReferenceLine x={0} stroke="var(--border-strong)" />
          <Bar dataKey="drift" radius={[0, 4, 4, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.onTrack ? "var(--status-success)" : "var(--status-warn)"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function BudgetVsTime({ budget }: { budget: AnalyticsSeed["budget"] }) {
  return (
    <div className="rounded-card border border-line bg-card p-4">
      <div className="space-y-3">
        <Bars label="Budget consumed" value={budget.consumedPct} tone="brand" />
        <Bars label="Time elapsed" value={budget.timeElapsedPct} tone="warn" />
      </div>
      <p className="mt-3 text-xs text-faint">Spend pace is {formatPct(budget.burnRateVsPlanPct, 1)} ahead of plan.</p>
    </div>
  );
}

function Bars({ label, value, tone }: { label: string; value: number; tone: "brand" | "warn" }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-mute">{label}</span>
        <span className="tabular-nums text-ink">{formatPct(value, 1)}</span>
      </div>
      <ProgressBar value={value} tone={tone} />
    </div>
  );
}

function OverrunBreakdown({ attributions }: { attributions: AnalyticsSeed["costDrivers"]["attributions"] }) {
  const total = attributions.reduce((s, a) => s + a.contributionPct, 0);
  return (
    <div className="rounded-card border border-line bg-card p-4">
      <p className="mb-2 text-xs font-medium text-faint">Overrun decomposition</p>
      <div className="flex h-6 overflow-hidden rounded-md">
        {attributions.map((a, i) => (
          <div
            key={a.factor}
            className="h-full"
            style={{
              width: `${(a.contributionPct / total) * 100}%`,
              background: ["var(--status-warn)", "var(--accent-base)", "var(--border-strong)"][i] ?? "var(--border-strong)",
            }}
            title={`${a.factor}: ${formatPct(a.contributionPct, 1)}`}
          />
        ))}
      </div>
      <ul className="mt-2 space-y-1">
        {attributions.map((a) => (
          <li key={a.factor} className="flex items-center justify-between text-xs">
            <span className="text-mute">{a.factor}{a.source ? ` · ${a.source}` : ""}</span>
            <span className="tabular-nums text-ink">{formatPct(a.contributionPct, 1)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
